import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Sparkle, Warning, X, ArrowRight } from '@phosphor-icons/react';

/**
 * Sticky banner shown at the top of every tenant page. Renders in three
 * modes based on the tenant's subscription state:
 *
 *   1. Trial (basic + `trial_ends_at`)     → indigo → amber (≤3 days)
 *   2. Paid plan approaching expiry (≤ 7 days) → amber ribbon
 *   3. Paid plan already expired            → red ribbon (undismissable)
 *
 * The pre-expiry warning threshold is 7 days as specified by the user.
 * Once the plan is fully expired the dismiss button is hidden — operator
 * needs to see it every page load until the admin renews.
 */
const WARN_THRESHOLD_DAYS = 7;

export default function TrialBanner() {
  const navigate = useNavigate();
  const location = useLocation();
  const [company, setCompany] = useState(null);
  const [plans, setPlans] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem('trial-banner-dismissed') === '1'; }
    catch { return false; }
  });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [c, p] = await Promise.all([
          api.get('/auth/me/company'),
          api.get('/plans').catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setCompany(c.data);
        setPlans(p.data || []);
      } catch {
        if (!cancelled) setCompany(null);
      }
    };
    load();
    window.addEventListener('company-updated', load);
    return () => { cancelled = true; window.removeEventListener('company-updated', load); };
  }, []);

  // Expired trial → the API rejects everything except billing, so park the
  // admin on the Plan & Billing page until a plan is activated.
  useEffect(() => {
    if (company?.trial_expired && location.pathname !== '/billing') navigate('/billing', { replace: true });
  }, [company, location.pathname, navigate]);

  if (!company) return null;

  const UpgradeBtn = ({ label = 'Upgrade now', testid = 'banner-upgrade-btn' }) => (
    <Link to="/billing" className="ml-1 inline-flex items-center gap-1 rounded-full bg-white/20 hover:bg-white/30 px-3 py-0.5 text-xs font-semibold" data-testid={testid}>
      {label} <ArrowRight size={12} weight="bold" />
    </Link>
  );

  if (company.trial_expired) {
    return (
      <div className="px-4 py-2 text-sm flex items-center justify-center gap-3 sticky top-0 z-30 bg-gradient-to-r from-red-600 to-rose-600 text-white" data-testid="trial-banner-expired">
        <Warning size={16} weight="fill" />
        <span><b>Your free trial has ended.</b> Activate a plan to continue using SMSuitePro.</span>
        <UpgradeBtn label="Choose a plan" testid="banner-choose-plan-btn" />
      </div>
    );
  }

  // Pricing chip — pull the Pro price from the live plans catalog when
  // available so we never drift from the source of truth on the backend.
  const proPlan = plans.find(p => p.id === 'pro');
  const proPrice = proPlan ? `₹${proPlan.price}/${proPlan.period}` : '₹999/mo';

  // ── Mode 1: paid plan expired (highest priority; overrides everything).
  if (company.plan_expired && company.plan_expires_at) {
    return (
      <div
        className="px-4 py-2 text-sm flex items-center justify-center gap-3 sticky top-0 z-30 bg-gradient-to-r from-red-600 to-rose-600 text-white"
        data-testid="expiry-banner-expired"
      >
        <Warning size={16} weight="fill" />
        <span>
          <b>Your subscription has expired.</b> Renew to keep new invoices flowing.
        </span>
        <UpgradeBtn label="Renew now" testid="banner-renew-btn" />
      </div>
    );
  }

  // ── Mode 2: paid plan approaching expiry (≤ 7 days).
  const planDaysLeft = company.plan_remaining_days;
  if (
    !dismissed
    && typeof planDaysLeft === 'number'
    && planDaysLeft <= WARN_THRESHOLD_DAYS
    && planDaysLeft >= 0
    && company.plan_expires_at
  ) {
    const dayLabel = planDaysLeft === 0
      ? 'today'
      : `in ${planDaysLeft} day${planDaysLeft === 1 ? '' : 's'}`;
    return (
      <div
        className="px-4 py-2 text-sm flex items-center justify-center gap-3 sticky top-0 z-30 bg-gradient-to-r from-amber-500 to-orange-500 text-white"
        data-testid="expiry-banner-warning"
      >
        <Warning size={15} weight="fill" />
        <span>
          Your <b>{company.plan_period || 'subscription'}</b> plan expires <b>{dayLabel}</b>.
          Renew soon to avoid interruption.
        </span>
        <UpgradeBtn label="Renew" testid="banner-renew-soon-btn" />
        <button
          type="button"
          onClick={() => {
            try { sessionStorage.setItem('trial-banner-dismissed', '1'); } catch { /* noop */ }
            setDismissed(true);
          }}
          aria-label="Dismiss"
          data-testid="expiry-banner-dismiss"
          className="ml-2 p-1 rounded hover:bg-white/15"
        >
          <X size={14} />
        </button>
      </div>
    );
  }

  // ── Mode 3: trial (existing behaviour, unchanged for backwards compat).
  if (dismissed) return null;
  if (company.subscription_plan !== 'basic') return null;
  if (!company.trial_ends_at) return null;

  const daysLeft = company.trial_remaining_days ?? 0;
  const urgent = daysLeft <= 3;
  const tone = urgent
    ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white'
    : 'bg-gradient-to-r from-indigo-500/95 to-fuchsia-500/95 text-white';

  return (
    <div className={`${tone} px-4 py-2 text-sm flex items-center justify-center gap-3 sticky top-0 z-30`} data-testid="trial-banner">
      <Sparkle size={15} weight="fill" />
      <span>
        <b>{daysLeft === 0 ? 'Last day' : `${daysLeft} day${daysLeft === 1 ? '' : 's'} left`}</b> on your free trial.
        Basic plan limits: <b>1 user</b>. Upgrade to Pro ({proPrice}) for unlimited usage.
      </span>
      <UpgradeBtn />
      <button
        type="button"
        onClick={() => {
          try { sessionStorage.setItem('trial-banner-dismissed', '1'); } catch { /* noop */ }
          setDismissed(true);
        }}
        aria-label="Dismiss"
        data-testid="trial-banner-dismiss"
        className="ml-2 p-1 rounded hover:bg-white/15"
      >
        <X size={14} />
      </button>
    </div>
  );
}
